import { Link } from 'react-router-dom';
import {
  EGB_PROD_ID,
  COUNCIL_ROLES,
  COUNCIL_FUNCTIONS,
  PRODUCT_VISION,
  PORTFOLIO_AREAS,
  LIFECYCLE_STAGES,
  FEATURE_GOVERNANCE,
  ROADMAP_TYPES,
  INNOVATION_AREAS,
  CUSTOMER_FEEDBACK,
  PRODUCT_DISCOVERY,
  PRIORITIZATION,
  BUSINESS_VALUE,
  PRODUCT_ANALYTICS,
  EXPERIMENTATION,
  PRODUCT_KPIS,
  RELEASE_PLANNING,
  DEPRECATION_GOVERNANCE,
  DOCUMENTATION,
  EXECUTIVE_DASHBOARD,
  GOVERNANCE_ANALYTICS,
  ACCESSIBILITY,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_COUNCIL,
  DEMO_VISION,
  DEMO_PORTFOLIO_LIFECYCLE,
  DEMO_FEATURE_GOVERNANCE,
  DEMO_ROADMAP,
  DEMO_INNOVATION_DISCOVERY,
  DEMO_PRIORITIZATION,
  DEMO_EXECUTIVE,
  buildProductDashboard,
} from '../../productgovernance/framework';
import {
  ProductCouncilPanel,
  ProductVisionPanel,
  PortfolioLifecyclePanel,
  FeatureGovernancePanel,
  RoadmapGovernancePanel,
  InnovationDiscoveryPanel,
  PrioritizationAnalyticsPanel,
  ExecutiveProductDashboardPanel,
} from '../../components/productgovernance';
import { Card, KpiCard } from '../../components/ui';

export function ProductGovernancePage() {
  const dashboard = buildProductDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EGB_PROD_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Product Governance</h1>
        <p className="mt-1 text-sm text-slate-500">
          Permanent Product Governance Framework — strategy, portfolio, innovation, and lifecycle from idea to retirement.
          Fully aligned with MPC-00001 and all Enterprise Governance Bibles.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Council Roles" value={dashboard.council_roles.toString()} />
        <KpiCard label="Portfolio Areas" value={dashboard.portfolio_areas.toString()} />
        <KpiCard label="Lifecycle Stages" value={dashboard.lifecycle_stages.toString()} />
        <KpiCard label="Roadmap Types" value={dashboard.roadmap_types.toString()} />
        <KpiCard label="Product KPIs" value={dashboard.product_kpis.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Product Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product Governance Council</h2>
        <p className="mt-1 text-sm text-slate-500">{COUNCIL_ROLES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{COUNCIL_FUNCTIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ProductCouncilPanel members={DEMO_COUNCIL} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Product Vision</h2>
        <p className="mt-1 text-sm text-slate-500">{PRODUCT_VISION.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ProductVisionPanel items={DEMO_VISION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Portfolio & Lifecycle</h2>
        <p className="mt-1 text-sm text-slate-500">{PORTFOLIO_AREAS.join(' · ')} · {LIFECYCLE_STAGES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <PortfolioLifecyclePanel items={DEMO_PORTFOLIO_LIFECYCLE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Feature Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{FEATURE_GOVERNANCE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <FeatureGovernancePanel items={DEMO_FEATURE_GOVERNANCE} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Roadmap Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{ROADMAP_TYPES.join(' · ')} · {RELEASE_PLANNING.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <RoadmapGovernancePanel items={DEMO_ROADMAP} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Innovation & Discovery</h2>
        <p className="mt-1 text-sm text-slate-500">
          {INNOVATION_AREAS.join(' · ')} · {PRODUCT_DISCOVERY.join(' · ')} · {CUSTOMER_FEEDBACK.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <InnovationDiscoveryPanel items={DEMO_INNOVATION_DISCOVERY} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Prioritization & Analytics</h2>
        <p className="mt-1 text-sm text-slate-500">
          {PRIORITIZATION.join(' · ')} · {BUSINESS_VALUE.join(' · ')} · {PRODUCT_ANALYTICS.join(' · ')} · {EXPERIMENTATION.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <PrioritizationAnalyticsPanel items={DEMO_PRIORITIZATION} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Executive Product Dashboard</h2>
        <p className="mt-1 text-sm text-slate-500">
          {PRODUCT_KPIS.join(' · ')} · {DEPRECATION_GOVERNANCE.join(' · ')} · {DOCUMENTATION.join(' · ')} · {EXECUTIVE_DASHBOARD.join(' · ')} · {GOVERNANCE_ANALYTICS.join(' · ')} · {ACCESSIBILITY.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <ExecutiveProductDashboardPanel items={DEMO_EXECUTIVE} />
        </Card>
      </section>
    </div>
  );
}
